import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@helios/auth-firebase-web';
import {
  PURCHASE_ORDER_PRINT_DEFAULTS,
  mapUIToPurchaseOrderPrintSettingsColumns,
  qAppConfig,
  qPurchaseOrderPrintSettings,
  type AppConfigRow,
  type PurchaseOrderPrintLogoSource,
  type PurchaseOrderPrintSettingsRow,
  type PurchaseOrderPrintSettingsUI,
} from '@helios/data-access';
import { PageHeader, useBrandTokens, type ShellOutletContext } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useFormatMoney } from '../../../hooks/useFormatMoney';
import { printHtmlViaIframe } from '../prescription-print/print-utils';
import { resolveBuyer, resolvePurchaseOrderSettings, toLayoutSettings } from './print-utils';
import {
  PurchaseOrderPrintLayout,
  type PurchaseOrderPrintLineItem,
  type PurchaseOrderPrintTotals,
} from './PurchaseOrderPrintLayout';

// ─── Sample data used to render the live preview ───
//
// Hardcoded so the preview is stable regardless of whether the org has
// any real POs yet. Real POs use the saved settings via
// PurchaseOrderPrintMount + the row-action Print button.

const SAMPLE_ITEMS: PurchaseOrderPrintLineItem[] = [
  {
    itemName: 'Paracetamol 500mg',
    sku: 'PARA-500',
    unit: 'box',
    quantityOrdered: 20,
    bonusQuantity: 2,
    rate: 280,
    amount: 5600,
    remarks: 'Lot expiring > 18 months preferred',
  },
  {
    itemName: 'Sterile Gauze Pads 10x10',
    sku: 'GAUZE-1010',
    unit: 'pack',
    quantityOrdered: 50,
    bonusQuantity: 0,
    rate: 120,
    amount: 6000,
  },
  {
    itemName: 'Gentle Cleanser 200ml',
    sku: 'CLNS-200',
    unit: 'bottle',
    quantityOrdered: 30,
    bonusQuantity: 3,
    rate: 320,
    amount: 9600,
  },
];

const SAMPLE_SUBTOTAL = SAMPLE_ITEMS.reduce((s, i) => s + i.amount, 0);
const SAMPLE_TOTALS: PurchaseOrderPrintTotals = {
  subtotal: SAMPLE_SUBTOTAL,
  totalAmount: SAMPLE_SUBTOTAL,
};
const SAMPLE_META = {
  poNumber: 'PO-PREVIEW-001',
  poDateIso: new Date().toISOString().slice(0, 10),
  status: 'posted',
  amountsAre: 'tax_exclusive',
  preparedBy: 'Front Desk',
};
const SAMPLE_NOTES =
  'Deliver to clinic loading bay between 9 AM and 5 PM, Monday–Friday. Coordinate with the procurement lead on arrival.';
const SAMPLE_SUPPLIER = {
  name: 'Acme Pharmaceutical Supply',
  contactPerson: 'Maria Santos',
  address: '12 Industrial Ave\nQuezon City, NCR',
  phone: '+63 2 8123 4567',
  email: 'orders@acmepharma.example',
  taxId: 'TIN-123-456-789',
  paymentTerms: 'Net 30',
};

// ─── A4 dimensions for the preview pane ───

const A4_WIDTH_PX = (210 * 96) / 25.4;
const A4_HEIGHT_PX = (297 * 96) / 25.4;

// ─── Page ───

export function PurchaseOrderPrintWizardPage() {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {};

  const fmt = useFormatMoney();
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const appConfig = appConfigRows[0] ?? null;

  const { data: settingsRows = [] } = usePowerSyncQuery<PurchaseOrderPrintSettingsRow>(
    qPurchaseOrderPrintSettings(),
  );
  const savedRow = settingsRows[0] ?? null;

  // Editor draft state. Initialized from the saved row (or defaults)
  // and only flushed to DB on Save — local typing stays snappy.
  const [draft, setDraft] = useState<Omit<PurchaseOrderPrintSettingsUI, 'id'> | null>(null);
  const [savedRowId, setSavedRowId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState(false);

  // Seed the editor draft when the saved row finishes loading. Doesn't
  // re-seed once the user is editing — the lastSeededIdRef guards
  // against PowerSync row identity churn while the user types.
  const seededFor = useMemo(() => savedRow?.id ?? 'defaults', [savedRow?.id]);
  useEffect(() => {
    // Only seed once per saved-row identity. The `draft` guard prevents
    // clobbering in-progress edits when PowerSync re-emits the same row;
    // `seededFor` is the single source of "the saved row that matters."
    if (draft) return;
    const settings = resolvePurchaseOrderSettings(savedRow);
    setSavedRowId(savedRow?.id ?? '');
    const { id: _id, ...rest } = settings;
    setDraft(rest);
  }, [seededFor, draft, savedRow]);

  const update = <K extends keyof Omit<PurchaseOrderPrintSettingsUI, 'id'>>(
    key: K,
    value: Omit<PurchaseOrderPrintSettingsUI, 'id'>[K],
  ) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSaveError(null);
  };

  const resetToDefaults = () => {
    setDraft({ ...PURCHASE_ORDER_PRINT_DEFAULTS });
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!draft || !db || saving) return;
    if (!orgId) {
      setSaveError('Missing organization context.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const cols = mapUIToPurchaseOrderPrintSettingsColumns(draft);
      const userId = user?.uid ?? null;
      if (savedRowId) {
        // Update existing row.
        await db.execute(
          `UPDATE purchase_order_print_settings
              SET logo_source = ?, buyer_name = ?, buyer_address = ?,
                  buyer_phone = ?, buyer_email = ?, buyer_tax_id = ?,
                  footer_text = ?, terms_text = ?,
                  prepared_by_label = ?, approved_by_label = ?,
                  supplier_acknowledgment_label = ?,
                  show_sku = ?, show_bonus = ?, show_remarks = ?,
                  show_supplier_contact = ?, show_signature_block = ?,
                  show_terms = ?,
                  updated_at = ?, updated_by = ?
            WHERE id = ?`,
          [
            cols.logo_source,
            cols.buyer_name,
            cols.buyer_address,
            cols.buyer_phone,
            cols.buyer_email,
            cols.buyer_tax_id,
            cols.footer_text,
            cols.terms_text,
            cols.prepared_by_label,
            cols.approved_by_label,
            cols.supplier_acknowledgment_label,
            cols.show_sku,
            cols.show_bonus,
            cols.show_remarks,
            cols.show_supplier_contact,
            cols.show_signature_block,
            cols.show_terms,
            cols.updated_at,
            userId,
            savedRowId,
          ],
        );
      } else {
        const newId = crypto.randomUUID();
        const now = new Date().toISOString();
        await db.execute(
          `INSERT INTO purchase_order_print_settings (
              id, org_id, clinic_id, logo_source,
              buyer_name, buyer_address, buyer_phone, buyer_email, buyer_tax_id,
              footer_text, terms_text,
              prepared_by_label, approved_by_label, supplier_acknowledgment_label,
              show_sku, show_bonus, show_remarks,
              show_supplier_contact, show_signature_block, show_terms,
              created_at, updated_at, updated_by
            ) VALUES (?, ?, NULL, ?,
              ?, ?, ?, ?, ?,
              ?, ?,
              ?, ?, ?,
              ?, ?, ?,
              ?, ?, ?,
              ?, ?, ?)`,
          [
            newId,
            orgId,
            cols.logo_source,
            cols.buyer_name,
            cols.buyer_address,
            cols.buyer_phone,
            cols.buyer_email,
            cols.buyer_tax_id,
            cols.footer_text,
            cols.terms_text,
            cols.prepared_by_label,
            cols.approved_by_label,
            cols.supplier_acknowledgment_label,
            cols.show_sku,
            cols.show_bonus,
            cols.show_remarks,
            cols.show_supplier_contact,
            cols.show_signature_block,
            cols.show_terms,
            now,
            cols.updated_at,
            userId,
          ],
        );
        setSavedRowId(newId);
      }
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 1800);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (!draft) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <PageHeader title="Purchase Order Print" collapsed={collapsed} onToggle={onToggleSidebar} />
        <div style={{ padding: 'var(--sp-6)', color: 'var(--text-muted)' }}>Loading…</div>
      </div>
    );
  }

  const previewSettings: PurchaseOrderPrintSettingsUI = { id: savedRowId, ...draft };
  const previewBuyer = resolveBuyer(previewSettings, appConfig);
  const previewLayoutSettings = toLayoutSettings(previewSettings);

  // Print Preview renders the sample PO into an isolated iframe (not a hidden
  // on-page mount), so the host page never leaks into the printout on tablet.
  const handlePrintPreview = () => {
    const html = renderToStaticMarkup(
      <PurchaseOrderPrintLayout
        settings={previewLayoutSettings}
        buyer={previewBuyer}
        supplier={SAMPLE_SUPPLIER}
        meta={SAMPLE_META}
        items={SAMPLE_ITEMS}
        totals={SAMPLE_TOTALS}
        notes={SAMPLE_NOTES}
        formatMoney={fmt}
      />,
    );
    printHtmlViaIframe(html, 'Purchase Order');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader title="Purchase Order Print" collapsed={collapsed} onToggle={onToggleSidebar} />

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: '1fr 1.05fr',
          gap: 'var(--sp-5)',
          padding: 'var(--sp-5) var(--sp-6) var(--sp-6)',
          overflow: 'hidden',
        }}
      >
        {/* Editor */}
        <div
          style={{
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-5)',
            paddingRight: 'var(--sp-2)',
          }}
        >
          <SectionCard title="Buyer / Branding">
            <Row label="Logo source">
              <LogoSelect value={draft.logoSource} onChange={(v) => update('logoSource', v)} />
            </Row>
            <Row label="Buyer name (override)">
              <TextField
                value={draft.buyerName}
                placeholder={appConfig?.org_name ?? ''}
                onChange={(v) => update('buyerName', v)}
              />
            </Row>
            <Row label="Buyer address">
              <TextArea
                value={draft.buyerAddress}
                placeholder={appConfig?.org_address ?? ''}
                onChange={(v) => update('buyerAddress', v)}
              />
            </Row>
            <Row label="Phone">
              <TextField
                value={draft.buyerPhone}
                placeholder={appConfig?.org_phone ?? ''}
                onChange={(v) => update('buyerPhone', v)}
              />
            </Row>
            <Row label="Email">
              <TextField
                value={draft.buyerEmail}
                placeholder={appConfig?.org_email ?? ''}
                onChange={(v) => update('buyerEmail', v)}
              />
            </Row>
            <Row label="Buyer Tax ID">
              <TextField value={draft.buyerTaxId} onChange={(v) => update('buyerTaxId', v)} />
            </Row>
            <HelpText>
              Buyer fields override app branding for printed POs. Leave a field blank to fall back
              to the org's app_config value.
            </HelpText>
          </SectionCard>

          <SectionCard title="Sections">
            <Toggle
              label="SKU column"
              checked={draft.showSku}
              onChange={(v) => update('showSku', v)}
            />
            <Toggle
              label="Bonus column"
              checked={draft.showBonus}
              onChange={(v) => update('showBonus', v)}
            />
            <Toggle
              label="Item remarks"
              checked={draft.showRemarks}
              onChange={(v) => update('showRemarks', v)}
            />
            <Toggle
              label="Supplier contact block"
              checked={draft.showSupplierContact}
              onChange={(v) => update('showSupplierContact', v)}
            />
            <Toggle
              label="Terms & notes block"
              checked={draft.showTerms}
              onChange={(v) => update('showTerms', v)}
            />
            <Toggle
              label="Signature / authorization block"
              checked={draft.showSignatureBlock}
              onChange={(v) => update('showSignatureBlock', v)}
            />
          </SectionCard>

          <SectionCard title="Document text">
            <Row label="Terms / conditions">
              <TextArea value={draft.termsText} onChange={(v) => update('termsText', v)} rows={4} />
            </Row>
            <Row label="Footer text">
              <TextArea
                value={draft.footerText}
                onChange={(v) => update('footerText', v)}
                rows={2}
              />
            </Row>
          </SectionCard>

          <SectionCard title="Signature labels">
            <Row label="Prepared by">
              <TextField
                value={draft.preparedByLabel}
                onChange={(v) => update('preparedByLabel', v)}
              />
            </Row>
            <Row label="Approved by">
              <TextField
                value={draft.approvedByLabel}
                onChange={(v) => update('approvedByLabel', v)}
              />
            </Row>
            <Row label="Supplier acknowledgment">
              <TextField
                value={draft.supplierAcknowledgmentLabel}
                onChange={(v) => update('supplierAcknowledgmentLabel', v)}
              />
            </Row>
          </SectionCard>

          {/* Action bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--sp-3) 0 var(--sp-6)',
              gap: 'var(--sp-3)',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={resetToDefaults}
              style={{
                height: 36,
                padding: '0 var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reset to defaults
            </button>
            <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
              {saveError && (
                <span style={{ fontSize: 'var(--font-size-xs)', color: '#b91c1c' }}>
                  {saveError}
                </span>
              )}
              {savedToast && (
                <span
                  style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-success-text)' }}
                >
                  Saved.
                </span>
              )}
              <button
                type="button"
                onClick={handlePrintPreview}
                style={{
                  height: 36,
                  padding: '0 var(--sp-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Print preview
              </button>
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving}
                style={{
                  height: 36,
                  padding: '0 var(--sp-4)',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: primary,
                  color: '#ffffff',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Save Layout'}
              </button>
            </div>
          </div>
        </div>

        {/* Preview pane */}
        <div
          style={{
            overflowY: 'auto',
            background: 'var(--bg-surface-2, #f3f4f6)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-4)',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: A4_WIDTH_PX,
              minHeight: A4_HEIGHT_PX,
              transform: 'scale(0.78)',
              transformOrigin: 'top center',
              boxShadow: '0 4px 24px rgba(15,23,42,0.10)',
              background: '#ffffff',
            }}
          >
            <PurchaseOrderPrintLayout
              settings={previewLayoutSettings}
              buyer={previewBuyer}
              supplier={SAMPLE_SUPPLIER}
              meta={SAMPLE_META}
              items={SAMPLE_ITEMS}
              totals={SAMPLE_TOTALS}
              notes={SAMPLE_NOTES}
              formatMoney={fmt}
              previewMode
            />
          </div>
        </div>
      </div>

    </div>
  );
}

// ─── Small editor primitives ───

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 'var(--sp-4)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 700,
          color: 'var(--text)',
          letterSpacing: 0.2,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function HelpText({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 'var(--font-size-xs)',
        color: 'var(--text-muted)',
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

const inputStyle: CSSProperties = {
  height: 36,
  padding: '0 var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--input-bg, #ffffff)',
  color: 'var(--text)',
  fontSize: 'var(--font-size-sm)',
  outline: 'none',
  fontFamily: 'inherit',
};

function TextField({
  value,
  placeholder,
  onChange,
}: {
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  return (
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
    />
  );
}

function TextArea({
  value,
  placeholder,
  rows = 3,
  onChange,
}: {
  value: string;
  placeholder?: string;
  rows?: number;
  onChange: (v: string) => void;
}) {
  return (
    <textarea
      value={value}
      placeholder={placeholder}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      style={{
        ...inputStyle,
        height: 'auto',
        padding: 'var(--sp-2) var(--sp-3)',
        resize: 'vertical',
        lineHeight: 1.5,
      }}
    />
  );
}

function LogoSelect({
  value,
  onChange,
}: {
  value: PurchaseOrderPrintLogoSource;
  onChange: (v: PurchaseOrderPrintLogoSource) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as PurchaseOrderPrintLogoSource)}
      style={{ ...inputStyle, cursor: 'pointer' }}
    >
      <option value="standard">Standard square logo</option>
      <option value="horizontal">Horizontal banner logo</option>
      <option value="none">No logo</option>
    </select>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        cursor: 'pointer',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--text)',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          width: 16,
          height: 16,
          accentColor: 'var(--color-primary)',
          cursor: 'pointer',
        }}
      />
      {label}
    </label>
  );
}
